import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import { artworkApi, CreateArtworkData, UpdateArtworkData } from '../api/artworkApi'
import { ArtworkStatus } from '@/types/entities'
import type { Artwork } from '@/types/entities'
import type { PaginatedResponse, PaginationParams } from '@/types/common'
import { useToast } from '@/hooks/use-toast'
import { trackArtworkPublished } from '@/lib/analytics'
import { getErrorMessage } from '@/lib/utils'

// Routes parse IDs as numbers, while PostgreSQL bigint IDs in API responses are strings.
// Use one key for both so successful mutations update the detail page already on screen.
function artworkQueryKey(id: number | string) {
  return ['artwork', Number(id)]
}

function updateArtworkCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  artworkId: number,
  patch: Partial<Artwork>
) {
  queryClient.setQueryData<Artwork>(artworkQueryKey(artworkId), (current) =>
    current ? { ...current, ...patch } : current
  )

  // Dashboard counts share this prefix but contain numbers, not paginated lists.
  // Throwing here aborts onMutate before the save/publication API call runs.
  queryClient.setQueriesData<PaginatedResponse<Artwork>>(
    { queryKey: ['artworks'], predicate: (query) => query.queryKey[1] !== 'count' },
    (current) => {
      if (!current) return current
      return {
        ...current,
        data: current.data.map((artwork) =>
          Number(artwork.id) === Number(artworkId) ? { ...artwork, ...patch } : artwork
        ),
      }
    }
  )
}

export function useArtworks(status: ArtworkStatus, pagination: PaginationParams) {
  return useQuery({
    queryKey: ['artworks', status, pagination],
    queryFn: () => artworkApi.getArtworks(status, pagination),
  })
}

export function useArtwork(artworkId: number) {
  return useQuery({
    queryKey: artworkQueryKey(artworkId),
    queryFn: () => artworkApi.getArtwork(artworkId),
    enabled: !!artworkId,
  })
}

export function useArtworkCount(status?: ArtworkStatus) {
  return useQuery({
    queryKey: ['artworks', 'count', status],
    queryFn: () => artworkApi.getArtworkCount(status),
  })
}

export function useCreateArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateArtworkData) => artworkApi.createArtwork(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      toast({
        title: 'Success',
        description: 'Artwork created successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to create artwork',
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateArtworkData }) =>
      artworkApi.updateArtwork(id, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: artworkQueryKey(variables.id) })
      const previousArtwork = queryClient.getQueryData<Artwork>(artworkQueryKey(variables.id))
      const previousArtworks = queryClient.getQueriesData<PaginatedResponse<Artwork>>({
        queryKey: ['artworks'],
      })

      // Tags go up as ids and come back as objects, so they are left to the server's answer.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tags: _tagIds, ...patch } = variables.data
      updateArtworkCaches(queryClient, variables.id, patch)

      return { previousArtwork, previousArtworks }
    },
    onError: (_error, variables, context) => {
      // Rollback optimistic updates
      if (context?.previousArtwork) {
        queryClient.setQueryData(artworkQueryKey(variables.id), context.previousArtwork)
      }
      if (context?.previousArtworks) {
        context.previousArtworks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      // Show error toast
      toast({
        title: 'Error',
        description: 'Failed to update artwork',
        variant: 'destructive',
      })
    },
    onSuccess: (updatedArtwork, variables) => {
      if (updatedArtwork) {
        updateArtworkCaches(queryClient, variables.id, updatedArtwork)
      }
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      queryClient.invalidateQueries({ queryKey: ['artworks', 'count'] })
      queryClient.invalidateQueries({ queryKey: artworkQueryKey(variables.id) })
      toast({
        title: 'Success',
        description: 'Artwork updated successfully',
      })
    },
  })
}

export function useDeleteArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (artworkId: number) => artworkApi.deleteArtwork(artworkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      toast({
        title: 'Success',
        description: 'Artwork deleted successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to delete artwork',
        variant: 'destructive',
      })
    },
  })
}

export function useUploadArtworkImage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  // Completed uploads are recorded once by the API and mirrored to PostHog.

  return useMutation({
    mutationFn: ({
      artworkId,
      file,
      onProgress,
    }: {
      artworkId: number
      file: File
      onProgress?: (progress: number) => void
    }) => artworkApi.uploadArtworkImage(artworkId, file, onProgress),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: artworkQueryKey(variables.artworkId) })
      toast({
        title: 'Success',
        description: 'Image uploaded successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      })
    },
  })
}

export function usePublishArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const posthog = usePostHog()

  return useMutation({
    mutationFn: (artworkId: number) => artworkApi.publishArtwork(artworkId),
    onMutate: async (artworkId) => {
      await queryClient.cancelQueries({ queryKey: artworkQueryKey(artworkId) })
      const previousArtwork = queryClient.getQueryData<Artwork>(artworkQueryKey(artworkId))
      const previousArtworks = queryClient.getQueriesData<PaginatedResponse<Artwork>>({
        queryKey: ['artworks'],
      })

      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.PUBLISHED })

      return { previousArtwork, previousArtworks }
    },
    onError: (error, artworkId, context) => {
      console.error('Failed to publish artwork:', error)
      if (context?.previousArtwork) {
        queryClient.setQueryData(artworkQueryKey(artworkId), context.previousArtwork)
      }
      if (context?.previousArtworks) {
        context.previousArtworks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast({
        title: 'Error',
        description: `Failed to publish artwork: ${getErrorMessage(error)}`,
        variant: 'destructive',
      })
    },
    onSuccess: (_, artworkId) => {
      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.PUBLISHED })
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      queryClient.invalidateQueries({ queryKey: ['artworks', 'count'] })
      queryClient.invalidateQueries({ queryKey: artworkQueryKey(artworkId) })
      trackArtworkPublished(posthog, { artwork_id: artworkId })
      toast({
        title: 'Success',
        description: 'Artwork published successfully',
      })
    },
  })
}

export function useUnpublishArtwork() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (artworkId: number) => artworkApi.unpublishArtwork(artworkId),
    onMutate: async (artworkId) => {
      await queryClient.cancelQueries({ queryKey: artworkQueryKey(artworkId) })
      const previousArtwork = queryClient.getQueryData<Artwork>(artworkQueryKey(artworkId))
      const previousArtworks = queryClient.getQueriesData<PaginatedResponse<Artwork>>({
        queryKey: ['artworks'],
      })

      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.DRAFT })

      return { previousArtwork, previousArtworks }
    },
    onError: (error, artworkId, context) => {
      console.error('Failed to unpublish artwork:', error)
      if (context?.previousArtwork) {
        queryClient.setQueryData(artworkQueryKey(artworkId), context.previousArtwork)
      }
      if (context?.previousArtworks) {
        context.previousArtworks.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      toast({
        title: 'Error',
        description: 'Failed to unpublish artwork',
        variant: 'destructive',
      })
    },
    onSuccess: (_, artworkId) => {
      updateArtworkCaches(queryClient, artworkId, { status: ArtworkStatus.DRAFT })
      queryClient.invalidateQueries({ queryKey: ['artworks'] })
      queryClient.invalidateQueries({ queryKey: ['artworks', 'count'] })
      queryClient.invalidateQueries({ queryKey: artworkQueryKey(artworkId) })
      toast({
        title: 'Success',
        description: 'Artwork unpublished successfully',
      })
    },
  })
}

